import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@indipia/ui-core';
import { ThumbImg } from './ThumbImg';
import { photoApi } from '../api/photoApi';

interface Photo {
  photoId: number;
  thumbUrl: string | null;
  originalUrl: string | null;
  takenAt: string | null;
  registeredAt?: string | null;
  tags: { tagType: string; tagValue: string }[];
}

interface Props {
  photos: Photo[];
  initialIndex: number;
  sitePhotoCount?: number;
  onClose: () => void;
  onDelete?: (photoId: number) => void;
  previewToken?: string;
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

const SPIN_CSS = '@keyframes _spin{to{transform:rotate(360deg)}}';

export function PhotoViewerLayer({ photos, initialIndex, sitePhotoCount, onClose, onDelete, previewToken }: Props) {
  const [index, setIndex] = useState(initialIndex);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const photo = photos[index];
  const src = photo?.originalUrl ?? photo?.thumbUrl ?? null;

  const prev = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIndex(i => Math.min(photos.length - 1, i + 1)), [photos.length]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // 수평 이동이 40px 이상이고 수직보다 클 때만 페이지 전환
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    if (deltaX < 0) next(); else prev();
  }, [next, prev]);

  useEffect(() => { setImgLoaded(false); setImgError(false); setDeleteConfirm(false); }, [index]);

  useEffect(() => {
    if (!src) { setBlobUrl(null); return; }
    let cancelled = false;
    let objectUrl: string | null = null;
    setBlobUrl(null);
    photoApi.getThumb(src, undefined, previewToken)
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setImgError(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, previewToken]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, onClose]);

  useEffect(() => {
    const saved = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = saved; };
  }, []);

  if (!photo) return null;

  const allTags = [...photo.tags].sort((a, b) => a.tagValue.localeCompare(b.tagValue, 'ko'));
  const count = sitePhotoCount ?? photos.length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="사진 뷰어"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 47,
        background: 'var(--alpha-black-700)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-family)',
      }}
    >
      <style>{SPIN_CSS}</style>

      {/* ── 이미지 카드 (래퍼: position relative → 태그/날짜를 absolute로 띄움) ── */}
      <div style={{ position: 'relative' }}>

        {/* 태그 + 날짜 — 이미지 바로 위에 absolute 고정, 이미지 위치에 영향 없음 */}
        {(allTags.length > 0 || photo.takenAt || photo.registeredAt) && (
          <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
            {allTags.length > 0 && (
              <div style={{ display: 'flex', gap: 'var(--num-8)', flexWrap: 'wrap', justifyContent: 'center', pointerEvents: 'auto' }}>
                {allTags.map(t => (
                  <span
                    key={`${t.tagType}-${t.tagValue}`}
                    style={{ padding: 'var(--num-4) var(--num-12)', borderRadius: 'var(--radius-full)', fontSize: 'var(--type-body-sm-size)', fontWeight: 700, background: 'var(--alpha-white-100)', color: 'var(--gray-0)', backdropFilter: 'blur(6px)', border: '1px solid var(--alpha-white-100)' }}
                  >
                    {t.tagValue}
                  </span>
                ))}
              </div>
            )}
            {(photo.takenAt || photo.registeredAt) && (
              <div style={{ display: 'flex', gap: 16 }}>
                {photo.takenAt && (
                  <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--alpha-white-400)' }}>
                    촬영일 : {fmtDate(photo.takenAt)}
                  </span>
                )}
                {photo.registeredAt && (
                  <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--alpha-white-400)' }}>
                    등록일 : {fmtDate(photo.registeredAt)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 화살표 + 이미지 카드 ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--num-16)' }}>
          {/* 이전 */}
          <button
            onClick={prev}
            disabled={index === 0}
            aria-label="이전 사진"
            style={{ width: 40, height: 40, borderRadius: 'var(--radius-full)', border: '1.5px solid var(--alpha-white-200)', background: 'var(--alpha-white-100)', color: 'var(--gray-0)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.25 : 1, flexShrink: 0, transition: 'all .13s' }}
            onMouseOver={e => { if (index > 0) (e.currentTarget as HTMLButtonElement).style.background = 'var(--alpha-white-200)'; }}
            onMouseOut={e => (e.currentTarget as HTMLButtonElement).style.background = 'var(--alpha-white-100)'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>

          {/* 이미지 카드 */}
          <div
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{ width: 'min(900px, 90vw)', height: 'min(680px, calc(100vh - 220px))', background: 'var(--shell-surface)', borderRadius: 'var(--radius-xl)', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--elevation-3)' }}
          >
            {/* 로딩 */}
            {src && !blobUrl && !imgError && (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--shell-border)" strokeWidth="1.8" strokeLinecap="round" style={{ animation: '_spin 1s linear infinite', position: 'absolute' }}>
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            )}

            {blobUrl && !imgError ? (
              <img
                key={blobUrl}
                src={blobUrl}
                alt=""
                onLoad={() => setImgLoaded(true)}
                onError={() => { setImgError(true); setImgLoaded(true); }}
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: imgLoaded ? 1 : 0, transition: 'opacity .2s' }}
              />
            ) : (
              imgError && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="var(--shell-border)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                  </svg>
                  <span style={{ fontSize: 12, color: 'var(--shell-hint)' }}>이미지를 불러올 수 없습니다</span>
                </div>
              )
            )}

            {/* 카드 우상단 버튼 그룹 */}
            <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
              {onDelete && (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  aria-label="사진 삭제"
                  style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: 'none', background: 'color-mix(in srgb, var(--color-error) 15%, transparent)', color: 'var(--color-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background .12s' }}
                  onMouseOver={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--color-error) 35%, transparent)')}
                  onMouseOut={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--color-error) 15%, transparent)')}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                  </svg>
                </button>
              )}
              <button
                onClick={onClose}
                aria-label="닫기"
                style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--alpha-black-100)', color: 'var(--shell-on-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background .12s' }}
                onMouseOver={e => (e.currentTarget.style.background = 'var(--alpha-black-200)')}
                onMouseOut={e => (e.currentTarget.style.background = 'var(--alpha-black-100)')}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          </div>

          {/* 다음 */}
          <button
            onClick={next}
            disabled={index === photos.length - 1}
            aria-label="다음 사진"
            style={{ width: 40, height: 40, borderRadius: 'var(--radius-full)', border: '1.5px solid var(--alpha-white-200)', background: 'var(--alpha-white-100)', color: 'var(--gray-0)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: index === photos.length - 1 ? 'default' : 'pointer', opacity: index === photos.length - 1 ? 0.25 : 1, flexShrink: 0, transition: 'all .13s' }}
            onMouseOver={e => { if (index < photos.length - 1) (e.currentTarget as HTMLButtonElement).style.background = 'var(--alpha-white-200)'; }}
            onMouseOut={e => (e.currentTarget as HTMLButtonElement).style.background = 'var(--alpha-white-100)'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      </div>

      {/* ── 하단: 현장 사진 수 + 썸네일 (absolute — 이미지 위치에 영향 없음) ── */}
      <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--alpha-white-600)' }}>
          {index + 1} <span style={{ fontWeight: 400, color: 'var(--alpha-white-400)' }}>/ {count}</span>
        </span>

        {/* 썸네일 스트립 */}
        <div style={{ display: 'flex', gap: 6, maxWidth: 'min(440px, 90vw)', overflowX: 'auto', paddingBottom: 2 }}>
          {photos.map((p, i) => {
            const thumb = p.thumbUrl ?? p.originalUrl;
            const active = i === index;
            return (
              <button
                key={p.photoId}
                onClick={() => setIndex(i)}
                aria-label={`${i + 1}번째 사진 보기`}
                style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', flexShrink: 0, padding: 0, overflow: 'hidden', border: `2px solid ${active ? 'var(--alpha-white-800)' : 'transparent'}`, background: 'var(--alpha-white-100)', cursor: 'pointer', transition: 'all .14s', opacity: active ? 1 : 0.5, outline: 'none' }}
              >
                <ThumbImg url={thumb} previewToken={previewToken} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} fallback={
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--alpha-white-300)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                    </svg>
                  </div>
                } />
              </button>
            );
          })}
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {deleteConfirm && onDelete && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="사진 삭제 확인"
          style={{ position: 'fixed', inset: 0, zIndex: 48, background: 'var(--alpha-black-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-family)' }}
          onClick={() => setDeleteConfirm(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-xl)', padding: '28px 28px 22px', width: 'min(340px, 92vw)', boxShadow: 'var(--elevation-3)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}
          >
            <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-error) 25%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </div>
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>사진을 삭제하시겠습니까?</div>
              <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)', lineHeight: 1.6 }}>삭제된 사진은 복구할 수 없습니다.</div>
            </div>
            <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => setDeleteConfirm(false)}
                style={{ flex: 1 }}
              >
                취소
              </Button>
              <Button
                variant="etc"
                size="lg"
                onClick={() => { setDeleteConfirm(false); onDelete(photo.photoId); }}
                style={{ flex: 1 }}
              >
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
